import { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { AllocationScenarioValuationModel, ValuationApproachModel } from 'api';
import { NewAllocationScenario } from 'common/types/allocation';
import { ValuationsApproach } from 'common/types/valuation';
import getGPCRowConfig from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/getRowConfig';
import { ReverseParsedValuationApproachGPC } from 'pages/Valuations/approaches/guidelinePublicCompanies/types';
import { GetPublicCompListBatchByDate } from 'pages/Valuations/components/RefreshGPCOption/RefreshGPCOption.props';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import { useGetPublicCompListBatchByDate } from 'services/hooks';
import { GetPublicCompsByDateHook } from 'services/hooks/valuations';
import { useValuationsStore } from 'store';
import { dbShortDate, getArrayValue, getObjectValue, getStringValue, gridShortDate, handleCatchError } from 'utilities';
import { refreshAllOpmRiskFreeRates } from './refreshAllOpmRiskFreeRates';
import type { UseGPCMarketDataRefreshParams } from './types';
import {
  filterPublicCompsApproaches,
  hasMarketDateChanged,
  isEquityOptionAvailable,
  shouldDisplayEquityOption,
  updatePublicCompsApproachSettings as updatePublicCompsApproachSettingsPure,
} from './utils';

/**
 * Manages public comps market data refresh lifecycle.
 *
 * Responsibilities:
 * - Detects market date/currency changes and implements Cap IQ/public comps fetch support
 *
 * This hook centralizes all market-date-driven Public Comps refresh logic that was
 * previously scattered across multiple useEffects in ValuationApproaches.tsx.
 *
 * @param params - Dependencies from ValuationApproaches component
 * @returns Handlers and utilities for public comps market data management
 */
export const usePCMarketDataRefresh = (params: UseGPCMarketDataRefreshParams) => {
  const {
    approaches,
    areSynchronized,
    companyInfo,
    compGroups,
    mergeStockMarketDataIntoApproaches, // this takes ISO-formatted date -> comparisons map object and uses it to update PC and BS approaches
    financialPeriods,
    getTickerListsByDate,
    isCurrentValuationDisabled,
    isLoadingPublicCompsByDate,
    marketCurrency,
    marketDate,
    publicCompsByDate,
    resetConfigurations,
    setApproaches,
    shouldSynchronizeApproaches,
    initiateStockMarketDataRefreshFlow,
    updateOPMInputs,
    valuation,
    workbook,
  } = params;
  const [currentPublicCompsByDate, setCurrentPublicCompsByDate] = useState<GetPublicCompsByDateHook['data']>(null);
  const [, fetchGPCCompListsByDate] = useGetPublicCompListBatchByDate() as GetPublicCompListBatchByDate;
  const setRefreshAllMarketDataHandler = useValuationsStore(state => state.setRefreshAllMarketDataHandler);

  // Refs: Track initial market conditions and fetch status
  const lastProcessedPublicCompsByDateRef = useRef<typeof publicCompsByDate | null>(null);
  const initialMarketDate = useRef(gridShortDate(marketDate));
  const initialMarketCurrency = useRef(marketCurrency);
  const approachesRef = useRef(approaches);
  approachesRef.current = approaches;

  const synchronizeRefreshedApproaches = useCallback(
    (refreshedApproaches: ValuationApproachModel[]) => {
      // Mutate valuation reference (matches existing pattern)
      // eslint-disable-next-line no-param-reassign
      valuation.valuations_approaches = [...refreshedApproaches];
      setApproaches([...refreshedApproaches]);
    },
    [valuation, setApproaches]
  );

  const applyOpmRiskFreeRates = useCallback(
    async (approachesToUpdate: ValuationApproachModel[]) => {
      const currentScenarios = getArrayValue(valuation?.allocation?.allocation_scenarios) as NewAllocationScenario[];
      const { approaches: approachesWithRates, allocationScenarios: scenariosWithRates } =
        await refreshAllOpmRiskFreeRates({
          approaches: approachesToUpdate as ValuationsApproach[],
          allocationScenarios: currentScenarios,
          marketDate,
        });
      if (valuation) {
        // eslint-disable-next-line no-param-reassign
        valuation.valuations_approaches = [...approachesWithRates];
        if (valuation.allocation) {
          // eslint-disable-next-line no-param-reassign
          valuation.allocation.allocation_scenarios = scenariosWithRates as AllocationScenarioValuationModel[];
        }
      }
      setApproaches([...approachesWithRates]);
      await updateOPMInputs({
        allocationScenarios: scenariosWithRates as AllocationScenarioValuationModel[],
        approaches: approachesWithRates,
      });
    },
    [marketDate, setApproaches, updateOPMInputs, valuation]
  );

  const refreshAllApproachesMarketData = useCallback(
    (publicCompsByDate: UseGPCMarketDataRefreshParams['publicCompsByDate'], marketDate: string) => {
      // Filters public comps approaches, applies new comparison data, and synchronizes
      // the updated approaches back to state.
      const publicCompsApproaches = filterPublicCompsApproaches(approaches);

      const refreshedApproaches = mergeStockMarketDataIntoApproaches({
        publicCompsDataByDate: publicCompsByDate,
        outdatedApproaches: publicCompsApproaches,
        approaches,
        marketDate: dbShortDate(marketDate) as string,
      });

      setCurrentPublicCompsByDate(publicCompsByDate);
      synchronizeRefreshedApproaches(refreshedApproaches);
    },
    [approaches, mergeStockMarketDataIntoApproaches, synchronizeRefreshedApproaches]
  );

  const updatePublicCompsApproachSettings = useCallback(updatePublicCompsApproachSettingsPure, []);

  const refreshAllMarketData = useCallback(async () => {
    if (isCurrentValuationDisabled) {
      return;
    }

    let refreshedApproaches = approachesRef.current as ValuationsApproach[];
    const publicCompsApproaches = filterPublicCompsApproaches(refreshedApproaches);

    if (publicCompsApproaches.length > 0 && financialPeriods) {
      const tickerListsByDate = getTickerListsByDate(
        publicCompsApproaches,
        refreshedApproaches,
        marketDate ? dbShortDate(marketDate) : undefined
      );
      const publicCompsDataByDate = await fetchGPCCompListsByDate?.(
        tickerListsByDate,
        marketCurrency,
        financialPeriods
      );
      if (publicCompsDataByDate) {
        refreshedApproaches = mergeStockMarketDataIntoApproaches({
          publicCompsDataByDate,
          outdatedApproaches: publicCompsApproaches,
          approaches: refreshedApproaches,
          marketDate: marketDate ? dbShortDate(marketDate) : undefined,
        }) as ValuationsApproach[];
      }
    }

    await applyOpmRiskFreeRates(refreshedApproaches);
  }, [
    applyOpmRiskFreeRates,
    fetchGPCCompListsByDate,
    financialPeriods,
    getTickerListsByDate,
    isCurrentValuationDisabled,
    marketCurrency,
    marketDate,
    mergeStockMarketDataIntoApproaches,
  ]);

  const refreshAllMarketDataRef = useRef(refreshAllMarketData);
  refreshAllMarketDataRef.current = refreshAllMarketData;

  useEffect(() => {
    const handler = () => refreshAllMarketDataRef.current();
    setRefreshAllMarketDataHandler(handler);
    return () => setRefreshAllMarketDataHandler(null);
  }, [setRefreshAllMarketDataHandler]);

  // Effect 1: Detect market date/currency changes and trigger refresh
  useEffect(() => {
    if (
      !hasMarketDateChanged({
        currentDate: marketDate,
        initialDate: initialMarketDate.current,
      })
    ) {
      return;
    }

    const pcApproaches = filterPublicCompsApproaches(approaches);
    const formattedMarketDate = gridShortDate(marketDate);

    if (pcApproaches.length > 0) {
      initiateStockMarketDataRefreshFlow({
        gpcApproaches: pcApproaches,
        currentApproaches: approaches,
        valuationDate: formattedMarketDate,
      });
    }

    if (!isCurrentValuationDisabled) {
      applyOpmRiskFreeRates(approachesRef.current).catch(handleCatchError);
    }

    // Update refs to new values
    initialMarketDate.current = formattedMarketDate;
    initialMarketCurrency.current = marketCurrency;
  }, [
    applyOpmRiskFreeRates,
    approaches,
    initiateStockMarketDataRefreshFlow,
    isCurrentValuationDisabled,
    marketCurrency,
    marketDate,
  ]);

  // We intentionally avoid auto-fetching Cap IQ data on initial load so the page reflects persisted DB comps first.

  // Effect 2: Refresh approaches when publicCompsByDate changes
  useEffect(() => {
    if (
      !isEmpty(publicCompsByDate) &&
      !isLoadingPublicCompsByDate &&
      !isEqual(publicCompsByDate, currentPublicCompsByDate) &&
      !shouldSynchronizeApproaches
    ) {
      refreshAllApproachesMarketData(publicCompsByDate, marketDate);
    }
  }, [
    currentPublicCompsByDate,
    isLoadingPublicCompsByDate,
    publicCompsByDate,
    refreshAllApproachesMarketData,
    shouldSynchronizeApproaches,
    marketDate,
  ]);

  // Effect 3: Reset public comps spreadsheet configurations when market data changes
  useEffect(() => {
    // Only run if publicCompsByDate has actually changed (not just a reference change)
    if (
      publicCompsByDate &&
      !isEmpty(publicCompsByDate) &&
      !isLoadingPublicCompsByDate &&
      !shouldSynchronizeApproaches &&
      workbook &&
      areSynchronized &&
      !isEqual(publicCompsByDate, lastProcessedPublicCompsByDateRef.current)
    ) {
      // Reset GPC spreadsheet configurations
      const pcApproaches = filterPublicCompsApproaches(approaches);

      pcApproaches.forEach(approach => {
        const approachTableName = getApproachTableName({ approach: approach as ValuationsApproach });
        const pcSpreadsheet = workbook[approachTableName];
        if (pcSpreadsheet?.tableData) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const tableData = getObjectValue(pcSpreadsheet.tableData) as any;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const approachData = getObjectValue(tableData?.approach) as any;
          const tableCompanyName = getStringValue(tableData?.companyName);
          const gpcApproach = approachData?.valuations_approach_gpc as ReverseParsedValuationApproachGPC;

          if (gpcApproach) {
            const rowConfigParams = {
              companyName: tableCompanyName || getStringValue(companyInfo?.name),
              approach: gpcApproach,
              allCompGroups: compGroups,
              isDisabled: isCurrentValuationDisabled,
              marketDate,
              publicCompsByDate: publicCompsByDate ?? undefined,
            };

            pcSpreadsheet.reset({
              rowConfig: getGPCRowConfig(rowConfigParams),
              tableData: {
                ...tableData,
                approach: {
                  ...approachData,
                  valuations_approach_gpc: gpcApproach,
                },
              },
            });
          }
        }
      });

      resetConfigurations();
      lastProcessedPublicCompsByDateRef.current = publicCompsByDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicCompsByDate, isLoadingPublicCompsByDate, shouldSynchronizeApproaches, areSynchronized]);

  return {
    // Handlers
    synchronizeRefreshedApproaches,
    refreshAllApproachesMarketData,
    updatePublicCompsApproachSettings,

    // Utilities
    isEquityOptionAvailable,
    shouldDisplayEquityOption,

    // State (for transparency/debugging)
    currentPublicCompsByDate,
  };
};
